<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store.js';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['created']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const name = ref('');
const description = ref('');
const selectedTemplate = ref('blank');
const isCreating = ref(false);

const templates = computed(() => [
  {
    key: 'blank',
    label: t('KANBAN.TEMPLATES.BLANK'),
    description: t('KANBAN.TEMPLATES.BLANK_DESCRIPTION'),
    steps: [],
  },
  {
    key: 'sales',
    label: t('KANBAN.TEMPLATES.SALES'),
    description: t('KANBAN.TEMPLATES.SALES_DESCRIPTION'),
    steps: [
      { name: t('KANBAN.TEMPLATES.SALES_STEPS.NEW_LEAD'), color: '#3B82F6' },
      { name: t('KANBAN.TEMPLATES.SALES_STEPS.QUALIFYING'), color: '#8B5CF6' },
      {
        name: t('KANBAN.TEMPLATES.SALES_STEPS.PROPOSAL_SENT'),
        color: '#F59E0B',
      },
      { name: t('KANBAN.TEMPLATES.SALES_STEPS.NEGOTIATION'), color: '#F97316' },
      {
        name: t('KANBAN.TEMPLATES.SALES_STEPS.LOST'),
        color: '#EF4444',
        cancelled: true,
      },
      { name: t('KANBAN.TEMPLATES.SALES_STEPS.WON'), color: '#22C55E' },
    ],
  },
  {
    key: 'support',
    label: t('KANBAN.TEMPLATES.SUPPORT'),
    description: t('KANBAN.TEMPLATES.SUPPORT_DESCRIPTION'),
    steps: [
      { name: t('KANBAN.TEMPLATES.SUPPORT_STEPS.NEW'), color: '#3B82F6' },
      {
        name: t('KANBAN.TEMPLATES.SUPPORT_STEPS.IN_PROGRESS'),
        color: '#F59E0B',
      },
      { name: t('KANBAN.TEMPLATES.SUPPORT_STEPS.WAITING'), color: '#8B5CF6' },
      { name: t('KANBAN.TEMPLATES.SUPPORT_STEPS.RESOLVED'), color: '#22C55E' },
    ],
  },
  {
    key: 'recruitment',
    label: t('KANBAN.TEMPLATES.RECRUITMENT'),
    description: t('KANBAN.TEMPLATES.RECRUITMENT_DESCRIPTION'),
    steps: [
      {
        name: t('KANBAN.TEMPLATES.RECRUITMENT_STEPS.APPLIED'),
        color: '#3B82F6',
      },
      {
        name: t('KANBAN.TEMPLATES.RECRUITMENT_STEPS.SCREENING'),
        color: '#8B5CF6',
      },
      {
        name: t('KANBAN.TEMPLATES.RECRUITMENT_STEPS.INTERVIEW'),
        color: '#F59E0B',
      },
      {
        name: t('KANBAN.TEMPLATES.RECRUITMENT_STEPS.OFFER'),
        color: '#F97316',
      },
      {
        name: t('KANBAN.TEMPLATES.RECRUITMENT_STEPS.HIRED'),
        color: '#22C55E',
      },
    ],
  },
]);

const open = () => {
  name.value = '';
  description.value = '';
  selectedTemplate.value = 'blank';
  dialogRef.value?.open();
};

const onConfirm = async () => {
  if (!name.value.trim()) return;

  isCreating.value = true;
  try {
    const template = templates.value.find(
      item => item.key === selectedTemplate.value
    );
    const board = await store.dispatch('kanban/createBoard', {
      name: name.value.trim(),
      description: description.value,
      steps: template?.steps || [],
    });
    dialogRef.value?.close();
    emit('created', board);
  } catch (error) {
    useAlert(error.message);
  } finally {
    isCreating.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KANBAN.NEW_BOARD.TITLE')"
    :confirm-button-label="t('KANBAN.NEW_BOARD.CREATE')"
    :cancel-button-label="t('KANBAN.NEW_BOARD.CANCEL')"
    :is-loading="isCreating"
    :disable-confirm-button="!name.trim()"
    overflow-y-auto
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="name"
        :label="t('KANBAN.NEW_BOARD.NAME_LABEL')"
        :placeholder="t('KANBAN.NEW_BOARD.NAME_PLACEHOLDER')"
      />
      <TextArea
        v-model="description"
        :label="t('KANBAN.NEW_BOARD.DESCRIPTION_LABEL')"
        :placeholder="t('KANBAN.NEW_BOARD.DESCRIPTION_PLACEHOLDER')"
      />
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.NEW_BOARD.TEMPLATE_LABEL') }}
        </span>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            v-for="template in templates"
            :key="template.key"
            type="button"
            class="flex flex-col gap-1 px-3 py-2 text-left border rounded-lg"
            :class="
              selectedTemplate === template.key
                ? 'border-n-brand bg-n-solid-blue'
                : 'border-n-weak hover:border-n-strong'
            "
            @click="selectedTemplate = template.key"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ template.label }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ template.description }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </Dialog>
</template>
